import { _ as _extends } from '../../_rollupPluginBabelHelpers-c245b26a.js';
import { forwardRef, useRef, useEffect } from 'react';
import { keyframes, jsx } from '@emotion/react';
import { themed } from '@indeed/ifl-css';
import { d as durations, e as easings } from '../../index-26fc6478.js';
import { DialogBackdrop } from './DialogBackdrop.js';
import { DialogBase } from './DialogBase.js';
import { useDialogContext } from './context.js';
import { Portal } from '../Portal/Portal.js';
import { AccessibleTrap } from '../AccessibleTrap/AccessibleTrap.js';
import 'focus-visible';
import './constants.js';
import '../Popup/Popup.js';
import '@floating-ui/react-dom';
import '../../hooks/useMergeRefs.js';
import '../Box/Box.js';
import '@indeed/ihl-use-id';
import '../../hooks/useControllableState.js';
import '../../utils/warn.js';
import '../../hooks/useIsMobile.js';
import '../../hooks/useWindowSize.js';
import '../../hooks/useIsomorphicLayoutEffect.js';
import '../../hooks/useResponsiveValue.js';
import '../../hooks/useBreakpointIndex.js';
import 'react-dom';
import 'react-remove-scroll';
import '../AccessibleTrap/FocusTrap.js';
import 'react-focus-lock';
import '../../hooks/useInertA11yBackground.js';
import '../../hooks/useForceUpdate.js';

/**
 * Dialog wrapper container styles
 */
const baseStyles = _ref => {
  let {
    isFullScreen,
    isMobile
  } = _ref;
  const customCSSProps = [];
  if (!isFullScreen && isMobile) {
    customCSSProps.push({
      paddingBlockStart: 7
    });
  }
  const animFadeIn = keyframes`
    0% { opacity: 0; }
    100% { opacity: 1; }
  `;
  return {
    animation: `${animFadeIn} ${durations.normal} ${easings.linear} forwards`,
    alignItems: !isFullScreen && isMobile ? "flex-end" : "center",
    blockSize: "100%",
    boxSizing: "border-box",
    display: "flex",
    inlineSize: "100%",
    isolation: "isolate",
    justifyContent: "center",
    left: 0,
    opacity: 0,
    paddingBlockEnd: isFullScreen || isMobile ? 0 : 4,
    paddingBlockStart: isFullScreen || isMobile ? 0 : 4,
    paddingInlineEnd: isFullScreen || isMobile ? 0 : 4,
    paddingInlineStart: isFullScreen || isMobile ? 0 : 4,
    position: "fixed",
    top: 0,
    zIndex: 10000,
    "@media (prefers-reduced-motion: reduce)": {
      animation: "none",
      opacity: 1
    },
    ...Object.assign({}, ...customCSSProps)
  };
};
const styleProps = new Set(["isFullScreen", "isMobile", "isOpen"]);
const StyledDialogContent = /*#__PURE__*/themed("div", process.env.NODE_ENV === "production" ? {
  styleProps: Array.from(styleProps),
  shouldForwardProp: prop => !styleProps.has(prop),
  target: "e1dz0cuo0"
} : {
  styleProps: Array.from(styleProps),
  shouldForwardProp: prop => !styleProps.has(prop),
  target: "e1dz0cuo0",
  label: "StyledDialogContent"
})(baseStyles, process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */");
const DialogContent = /*#__PURE__*/forwardRef((_ref2, forwardedRef) => {
  let {
    children,
    ...remainingProps
  } = _ref2;
  const internalRef = useRef(null);
  const {
    accessibleTrapProps,
    id,
    isFullScreen,
    isMobile,
    isOpen,
    portaled,
    setOpen,
    type
  } = useDialogContext();

  // Add Escape key support for dialog closing
  useEffect(() => {
    const onKeyPress = event => {
      if (event.key === "Escape") {
        if (internalRef && internalRef.current) {
          /**
           * Validate that the current active element is within the dialog to avoid early
           * termination with active elements outside the modal (like portals) and
           * validate that current active modal is the topmost modal before closing
           */
          if (internalRef.current.contains(event.target) && !internalRef.current.querySelector("div[role='dialog']")) {
            event.stopImmediatePropagation();
            setOpen(!isOpen);
          }
        }
      }
    };
    const cb = event => onKeyPress(event);
    if (isOpen && type !== "alertdialog") {
      window.addEventListener("keydown", cb);
    }
    return () => {
      window.removeEventListener("keydown", cb);
    };
  }, [isOpen, setOpen, type]);

  // Default dialog wrapper with Focus Trap and Backdrop
  const renderDialogWrapper = () => jsx(AccessibleTrap, _extends({}, accessibleTrapProps != null && accessibleTrapProps.allowList ? {
    hideBackground: {
      allowList: accessibleTrapProps == null ? void 0 : accessibleTrapProps.allowList
    }
  } : null, {
    disableScroll: type !== "popup" || isMobile,
    disabled: !isOpen,
    focusWrapperStyle: {
      position: "absolute"
    },
    restoreFocus: true,
    restoreFocusRef: accessibleTrapProps == null ? void 0 : accessibleTrapProps.restoreFocusRef,
    initializeFocusRef: (accessibleTrapProps == null ? void 0 : accessibleTrapProps.initializeFocusRef) ?? internalRef
  }), jsx(StyledDialogContent, _extends({
    id: id,
    isFullScreen: isFullScreen,
    isMobile: isMobile,
    ref: forwardedRef
  }, remainingProps), jsx(DialogBase, {
    ref: internalRef,
    role: type === "alertdialog" ? "alertdialog" : "dialog",
    tabIndex: -1
  }, children), jsx(DialogBackdrop, null)));

  // Conditionally render the component to properly register accessible trap
  return !isOpen ? null : portaled ? jsx(Portal, null, renderDialogWrapper()) : renderDialogWrapper();
});
DialogContent.displayName = "DialogContent";

export { DialogContent, StyledDialogContent, baseStyles };
